import { applySheets } from "../../../app/utils/styleCache.js";
import { isWerkstudent } from "../../../app/auth.js";

const WERKSTUDENT_WEEKLY_LIMIT_MINUTES = 20 * 60;

function parseHHMM(str) {
    if (!str) return 0;
    const parts = String(str).split(":");
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
}

function minutesToHHMM(total) {
    const roundedMinutes = Math.round(total);
    return `${String(Math.floor(roundedMinutes / 60)).padStart(2, "0")}:${String(roundedMinutes % 60).padStart(2, "0")}`;
}

function hoursToHHMM(hours) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${String(wholeHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function mondayOfWeek(dateStr) {
    const date = new Date(`${dateStr}T00:00:00`);
    const day = date.getDay();
    date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
    return date.toISOString().split("T")[0];
}

function maxWeeklyMinutes(rows) {
    const weeklyTotals = {};
    rows.forEach(row => {
        if (!row.date) return;
        const weekKey = mondayOfWeek(row.date);
        weeklyTotals[weekKey] = (weeklyTotals[weekKey] || 0) + parseHHMM(row.duration);
    });
    return Math.max(0, ...Object.values(weeklyTotals));
}

function minutesToSignedHMM(totalMinutes) {
    const rounded = Math.round(totalMinutes / 2) * 2;
    const sign = rounded < 0 ? "-" : "+";
    const abs = Math.abs(rounded);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

const cardConfigs = {
    entries: [
        { id: "cardTotal",      label: "Stunden | Gesamt",             value: "--:--", accent: false },
        { id: "cardAvg",        label: "Stunden | Durchschnitt",       value: "--:--", accent: false },
    ],
    timelogs: [
        { id: "cardWorkTotal",  label: "Arbeitszeit | Gesamt",         value: "--:--", accent: false, reserveSub: true },
        { id: "cardWorkAvg",    label: "Arbeitszeit | Durchschnitt",   value: "--:--", accent: false, reserveSub: true },
        { id: "cardBreakTotal", label: "Pausenzeit | Gesamt",          value: "--:--", accent: false, reserveSub: true },
        { id: "cardBreakAvg",   label: "Pausenzeit | Durchschnitt",    value: "--:--", accent: false, reserveSub: true }
    ],
};

export class AppSummary extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.isMounted = false;
        this.cards = {};
        this.type = "entries";
    }

    connectedCallback() {
        this.type = this.getAttribute("type") ?? "entries";
        const configs = cardConfigs[this.type] ?? cardConfigs.entries;

        const cardHTML = configs.map(c =>
            `<app-card id="${c.id}" label="${c.label}" value="${c.value}"${c.accent ? " accent" : ""}${c.reserveSub ? " reserve-sub" : ""}></app-card>`
        ).join("\n                    ");

        this.shadowRoot.innerHTML = `
            <style>:host { opacity: 0; }</style>
            <div class="content">
                <div class="cards">
                    ${cardHTML}
                </div>
            </div>
        `;

        this.isMounted = true;

        const root = this.shadowRoot;
        configs.forEach(c => { this.cards[c.id] = root.querySelector(`#${c.id}`); });

        this.ready = applySheets(this.shadowRoot, ["components/features/app-summary/app-summary.css"])
            .then(() => new Promise(resolve => requestAnimationFrame(resolve)));
    }

    updateStats(rows) {
        if (!this.isMounted) return;

        if (this.type === "entries") {
            const filtered = rows.filter(r => r.workedHours != null);
            const count = filtered.length;
            const total = filtered.reduce((sum, r) => sum + (r.workedHours || 0), 0);
            const avg = count > 0 ? total / count : 0;
            this.cards.cardTotal?.setAttribute("value", hoursToHHMM(total));
            this.cards.cardAvg?.setAttribute("value", hoursToHHMM(avg));
        } else if (this.type === "timelogs") {
            const totalWorkMin = rows.reduce((sum, row) => sum + parseHHMM(row.duration), 0);
            const totalBreakMin = rows.reduce((sum, row) => sum + parseHHMM(row.breakTime), 0);

            const avgEligibleRows = rows.filter(row => !row.isDayOff);
            const count = avgEligibleRows.length;
            const avgWorkMin = count > 0 ? avgEligibleRows.reduce((sum, row) => sum + parseHHMM(row.duration), 0) / count : 0;
            const avgBreakMin = count > 0 ? avgEligibleRows.reduce((sum, row) => sum + parseHHMM(row.breakTime), 0) / count : 0;

            this.cards.cardWorkTotal?.setAttribute("value", minutesToHHMM(totalWorkMin));

            if (isWerkstudent()) {
                const worstWeekMin = maxWeeklyMinutes(rows);
                if (worstWeekMin > WERKSTUDENT_WEEKLY_LIMIT_MINUTES) {
                    this.cards.cardWorkTotal?.setAttribute("sub", `Über 20 Std./Woche gearbeitet (${minutesToHHMM(worstWeekMin)})`);
                    this.cards.cardWorkTotal?.setAttribute("warn", "");
                } else {
                    this.cards.cardWorkTotal?.removeAttribute("sub");
                    this.cards.cardWorkTotal?.removeAttribute("warn");
                }
            } else {
                const closedRows = rows.filter(row => row.end);
                const saldoEligibleWorkMin = closedRows
                    .filter(row => !row.isAutoGeneratedAbsence)
                    .reduce((sum, row) => sum + parseHHMM(row.duration), 0);
                const targetWorkMin = closedRows.filter(row => !row.isDayOff).length * 8 * 60;
                this.cards.cardWorkTotal?.setAttribute("sub", `Saldo: ${minutesToSignedHMM(saldoEligibleWorkMin - targetWorkMin)}`);
                this.cards.cardWorkTotal?.removeAttribute("warn");
            }

            this.cards.cardWorkAvg?.setAttribute("value", minutesToHHMM(avgWorkMin));
            this.cards.cardBreakTotal?.setAttribute("value", minutesToHHMM(totalBreakMin));
            this.cards.cardBreakAvg?.setAttribute("value", minutesToHHMM(avgBreakMin));
        }
    }
}

customElements.define("app-summary", AppSummary);